import React from 'react';
import { motion } from 'framer-motion';
import {
  ChefHat, Book, Camera, Heart, Users, Share2, Apple, CheckCircle2, ChevronRight,
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, Eyebrow, FadeIn } from '../components/ui-bits';
import Breadcrumb from '../components/Breadcrumb';

const LOGO = 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/jhamaw9s_logo%20recetario.png';

const T = {
  es: {
    eyebrow: 'App · Recetario',
    heroTitle: 'Las recetas de la familia,',
    heroTitleAccent: 'y las historias que las acompañan.',
    heroSubtitle: 'Recetario es un libro de cocina familiar virtual: guarda las recetas que se cocinan hace generaciones, y junto a cada una, los momentos que las hicieron memorables. La nona, el asado del abuelo, la primera Pascua juntos.',
    contactCta: 'Escribir a info@tair.site',
    features: 'Características',
    featuresTitle: 'Una receta, cien recuerdos.',
    how: 'Cómo funciona',
    howTitle: 'Fácil como abrir el cuaderno de la abuela.',
    coverage: 'Lo que puedes guardar',
    coverageTitle: 'Más que ingredientes: memoria familiar.',
    trust1: 'Privado por defecto',
    trust2: 'Solo para tu familia',
    trust3: 'Guarda para siempre',
  },
  en: {
    eyebrow: 'App · Recetario',
    heroTitle: 'The family recipes,',
    heroTitleAccent: 'and the stories behind them.',
    heroSubtitle: 'Recetario is a virtual family cookbook: keep the recipes cooked for generations, and next to each one, the moments that made them memorable. Grandma\u2019s bake, grandpa\u2019s barbecue, the first Passover together.',
    contactCta: 'Write to info@tair.site',
    features: 'Features',
    featuresTitle: 'One recipe, a hundred memories.',
    how: 'How it works',
    howTitle: 'As easy as opening grandma\u2019s notebook.',
    coverage: 'What you can save',
    coverageTitle: 'More than ingredients: family memory.',
    trust1: 'Private by default',
    trust2: 'Only for your family',
    trust3: 'Kept forever',
  },
};

const Recetario = () => {
  const { lang } = useLanguage();
  const s = T[lang] || T.es;

  const features = [
    { icon: Book, t: lang === 'en' ? 'Full recipes' : 'Recetas completas', d: lang === 'en' ? 'Ingredients, steps, times and yields for every dish.' : 'Ingredientes, pasos, tiempos y porciones para cada plato.' },
    { icon: Camera, t: lang === 'en' ? 'Photo memories' : 'Recuerdos con foto', d: lang === 'en' ? 'Add photos of the dish, the cook and the meal it was shared at.' : 'Agrega fotos del plato, del cocinero y de la comida donde se compartió.' },
    { icon: Heart, t: lang === 'en' ? 'Family stories' : 'Historias familiares', d: lang === 'en' ? 'Attach the story behind each recipe: who cooked it, when, why.' : 'Adjunta la historia detrás de cada receta: quién la cocinó, cuándo, por qué.' },
    { icon: Users, t: lang === 'en' ? 'Shared with family' : 'Compartido con la familia', d: lang === 'en' ? 'Invite parents, siblings and children to add, edit and celebrate together.' : 'Invita a padres, hermanos e hijos a agregar, editar y celebrar juntos.' },
    { icon: ChefHat, t: lang === 'en' ? 'Occasion tags' : 'Etiquetas por ocasión', d: lang === 'en' ? 'Tag by holiday, celebration or season so nothing gets lost in time.' : 'Etiqueta por festividad, celebración o estación para que nada se pierda con el tiempo.' },
    { icon: Share2, t: lang === 'en' ? 'Family cookbook export' : 'Libro familiar exportable', d: lang === 'en' ? 'Export the collection as a printable book to keep or give as a gift.' : 'Exporta la colección como libro imprimible para guardar o regalar.' },
  ];

  const items = lang === 'en' ? [
    'Traditional and modern recipes, from grandma to the youngest chef',
    'Photos of the dish, the cooking process and the family meal',
    'Personal notes: "this was my dad\u2019s favourite", "always burn the sugar"',
    'Historical moments: birthdays, Passover, Rosh Hashaná, weddings',
    'Original author, adaptations by generation, and dietary variants',
    'A private tree of who taught the recipe to whom',
  ] : [
    'Recetas tradicionales y modernas, de la abuela al más joven de la casa',
    'Fotos del plato, del proceso y de la comida familiar donde se cocinó',
    'Notas personales: "era la favorita de mi papá", "siempre quemar el azúcar"',
    'Momentos históricos: cumpleaños, Pésaj, Rosh Hashaná, matrimonios',
    'Autor original, adaptaciones por generación y variantes por dieta',
    'Un árbol privado de quién le enseñó la receta a quién',
  ];

  const steps = lang === 'en' ? [
    { n: '01', t: 'Create your family', d: 'Invite the members that will share your recipe book.' },
    { n: '02', t: 'Add your first recipe', d: 'Photo, ingredients, steps — and the story behind it.' },
    { n: '03', t: 'Cook and pass it on', d: 'Every dish keeps a memory. Every memory keeps a family together.' },
  ] : [
    { n: '01', t: 'Crea tu familia', d: 'Invita a los integrantes que compartirán el recetario.' },
    { n: '02', t: 'Agrega tu primera receta', d: 'Foto, ingredientes, pasos — y la historia detrás.' },
    { n: '03', t: 'Cocina y transmite', d: 'Cada plato guarda un recuerdo. Cada recuerdo mantiene unida a la familia.' },
  ];

  return (
    <div data-testid="recetario-marketing">
      <Breadcrumb items={[
        { to: '/que-hacemos', label: lang === 'en' ? 'What We Do' : 'Qué Hacemos' },
        { to: '/que-hacemos/desarrollo-software', label: 'Software' },
        { to: null, label: 'Recetario' },
      ]} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 light-beam" />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 lg:px-12 pt-4 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <FadeIn><Eyebrow>{s.eyebrow}</Eyebrow></FadeIn>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                className="mt-8 font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              >
                {s.heroTitle}
                <br />
                <span className="text-amber-300">{s.heroTitleAccent}</span>
              </motion.h1>
              <FadeIn delay={0.25}>
                <p className="mt-8 max-w-xl font-body text-base sm:text-lg text-white/65 leading-relaxed">{s.heroSubtitle}</p>
              </FadeIn>
              <FadeIn delay={0.35}>
                <div className="mt-10 flex flex-wrap gap-3">
                  <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer" data-testid="recetario-appstore" className="inline-flex items-center gap-3 h-13 px-6 py-3 rounded-full bg-white text-[#0a1322] font-semibold text-sm hover:bg-amber-100 transition-colors">
                    <Apple className="w-5 h-5" />
                    <div className="flex flex-col items-start leading-tight">
                      <span className="text-[10px] uppercase tracking-widest text-black/60">{lang === 'en' ? 'Download on the' : 'Descargar en'}</span>
                      <span>App Store</span>
                    </div>
                  </a>
                  <a href="mailto:info@tair.site" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-transparent border border-white/20 text-white/85 font-medium text-sm hover:bg-white/5 hover:border-white/40 transition-colors">{s.contactCta}</a>
                </div>
              </FadeIn>
              <FadeIn delay={0.5}>
                <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/50">
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust1}</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust2}</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust3}</span>
                </div>
              </FadeIn>
            </div>
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <FadeIn delay={0.35}>
                <div className="relative">
                  <div className="absolute inset-0 blur-3xl bg-rose-400/20 rounded-full" />
                  <div className="relative w-56 h-56 md:w-72 md:h-72 rounded-[3rem] overflow-hidden shadow-2xl border border-white/10 bg-[#0a1322]">
                    <img src={LOGO} alt="Recetario" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 text-[10px] uppercase tracking-widest text-white/40 bg-[#070d18]/80 border border-white/10 rounded-full px-3 py-1.5 backdrop-blur">iOS · iPhone · iPad</div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      <Section className="py-24">
        <FadeIn><Eyebrow>{s.features}</Eyebrow></FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl leading-[1.1]">{s.featuresTitle}</h2>
        </FadeIn>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <FadeIn key={i} delay={i * 0.06}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover">
                  <div className="w-11 h-11 rounded-xl bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-300"><Icon className="w-5 h-5" /></div>
                  <h4 className="mt-6 font-display font-bold text-lg md:text-xl text-white tracking-tight">{f.t}</h4>
                  <p className="mt-3 font-body text-sm text-white/55 leading-relaxed">{f.d}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      <Section className="pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-5">
            <Eyebrow>{s.coverage}</Eyebrow>
            <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl text-white leading-[1.1]">{s.coverageTitle}</h2>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-7">
            <ul className="space-y-3">
              {items.map((c, i) => (
                <li key={i} className="flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-5 card-hover">
                  <ChevronRight className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />
                  <span className="text-white/80 font-body">{c}</span>
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </Section>

      <Section className="pb-32">
        <FadeIn><Eyebrow>{s.how}</Eyebrow></FadeIn>
        <FadeIn delay={0.08}>
          <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl leading-[1.1]">{s.howTitle}</h2>
        </FadeIn>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {steps.map((step, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover">
                <div className="font-display text-amber-300 text-sm tracking-widest">{step.n}</div>
                <h4 className="mt-4 font-display font-bold text-xl text-white tracking-tight">{step.t}</h4>
                <p className="mt-3 font-body text-white/60 leading-relaxed">{step.d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default Recetario;
